import { CakeIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

/**
 * TodaySummaryCard Component
 *
 * Displays today's nutrition summary with goal progress bars
 * Shows calories, protein, carbs, and fat with visual progress indicators
 */
export default function TodaySummaryCard({
  calories = 0,
  protein = 0,
  carbs = 0,
  fat = 0,
  goals = {
    calories: 2000,
    protein: 50,
    carbs: 250,
    fat: 75
  },
  mealCount = 0,
  date = new Date()
}) {
  const { t, i18n } = useTranslation()

  // Calculate percentages
  const caloriesPercent = goals.calories > 0 ? (calories / goals.calories) * 100 : 0
  const proteinPercent = goals.protein > 0 ? (protein / goals.protein) * 100 : 0
  const carbsPercent = goals.carbs > 0 ? (carbs / goals.carbs) * 100 : 0
  const fatPercent = goals.fat > 0 ? (fat / goals.fat) * 100 : 0

  // Calculate overall goal adherence (average of all percentages)
  const overallAdherence = Math.round((caloriesPercent + proteinPercent + carbsPercent + fatPercent) / 4)

  // Get color class based on percentage
  const getColorClass = (percent) => {
    if (percent >= 80 && percent <= 120) return 'bg-green-500' // Optimal
    if ((percent >= 60 && percent < 80) || (percent > 120 && percent <= 150)) return 'bg-amber-500' // Caution
    return 'bg-red-500' // Warning
  }

  // Get text color class based on percentage
  const getTextColorClass = (percent) => {
    if (percent >= 80 && percent <= 120) return 'text-green-600' // Optimal
    if ((percent >= 60 && percent < 80) || (percent > 120 && percent <= 150)) return 'text-amber-600' // Caution
    return 'text-red-600' // Warning
  }

  // Get locale for date formatting
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US'

  // Format date
  const formattedDate = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date)

  // Format date for title (shorter format)
  const titleDate = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)

  return (
    <div className="vh-panel bg-gradient-to-br from-indigo-50 to-surface-container rounded-lg shadow-md p-6 border border-indigo-100">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('nutrition.title')}, {titleDate}</h2>
          <p className="text-sm text-gray-600 mt-1">{formattedDate}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-indigo-600">{overallAdherence}%</div>
          <div className="text-xs text-gray-600">{t('nutrition.goalAdherence', 'Goal Adherence')}</div>
        </div>
      </div>

      {/* Macros Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Calories */}
        <div className="bg-surface-container rounded-lg p-4 shadow-sm border border-gray-200">
          <div className={`text-3xl font-bold ${getTextColorClass(caloriesPercent)}`}>
            {Math.round(calories)} <span className="text-lg text-indigo-400 font-normal">/ {goals.calories}</span>
          </div>
          <div className="text-xs text-gray-600 mb-2">{t('nutrition.calories')}</div>

          {/* Progress Bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getColorClass(caloriesPercent)} transition-all duration-300`}
              style={{ width: `${Math.min(caloriesPercent, 100)}%` }}
            />
          </div>

          <div className="text-xs text-gray-500 mt-1">
            {Math.round(caloriesPercent)}% {t('nutrition.ofGoal', 'of goal')}
          </div>
        </div>

        {/* Protein */}
        <div className="bg-surface-container rounded-lg p-4 shadow-sm border border-gray-200">
          <div className={`text-3xl font-bold ${getTextColorClass(proteinPercent)}`}>
            {protein.toFixed(1)}g <span className="text-lg text-blue-400 font-normal">/ {goals.protein}g</span>
          </div>
          <div className="text-xs text-gray-600 mb-2">{t('nutrition.protein')}</div>

          {/* Progress Bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getColorClass(proteinPercent)} transition-all duration-300`}
              style={{ width: `${Math.min(proteinPercent, 100)}%` }}
            />
          </div>

          <div className="text-xs text-gray-500 mt-1">
            {Math.round(proteinPercent)}% {t('nutrition.ofGoal', 'of goal')}
          </div>
        </div>

        {/* Carbs */}
        <div className="bg-surface-container rounded-lg p-4 shadow-sm border border-gray-200">
          <div className={`text-3xl font-bold ${getTextColorClass(carbsPercent)}`}>
            {carbs.toFixed(1)}g <span className="text-lg text-green-400 font-normal">/ {goals.carbs}g</span>
          </div>
          <div className="text-xs text-gray-600 mb-2">{t('nutrition.carbs')}</div>

          {/* Progress Bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getColorClass(carbsPercent)} transition-all duration-300`}
              style={{ width: `${Math.min(carbsPercent, 100)}%` }}
            />
          </div>

          <div className="text-xs text-gray-500 mt-1">
            {Math.round(carbsPercent)}% {t('nutrition.ofGoal', 'of goal')}
          </div>
        </div>

        {/* Fat */}
        <div className="bg-surface-container rounded-lg p-4 shadow-sm border border-gray-200">
          <div className={`text-3xl font-bold ${getTextColorClass(fatPercent)}`}>
            {fat.toFixed(1)}g <span className="text-lg text-amber-400 font-normal">/ {goals.fat}g</span>
          </div>
          <div className="text-xs text-gray-600 mb-2">{t('nutrition.fat')}</div>

          {/* Progress Bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getColorClass(fatPercent)} transition-all duration-300`}
              style={{ width: `${Math.min(fatPercent, 100)}%` }}
            />
          </div>

          <div className="text-xs text-gray-500 mt-1">
            {Math.round(fatPercent)}% {t('nutrition.ofGoal', 'of goal')}
          </div>
        </div>
      </div>

      {/* Meal Count */}
      <div className="flex items-center text-sm text-gray-600 mt-4">
        <CakeIcon className="h-5 w-5 mr-2 text-indigo-600" />
        <span className="font-medium">
          {mealCount} {mealCount === 1 ? t('nutrition.mealLogged', 'meal logged') : t('nutrition.mealsLogged', 'meals logged')}
        </span>
      </div>

    </div>
  )
}
